import React, { useContext, useEffect, useState } from 'react';
import UserContext from './UserContext';
import { Alert } from '@mui/material';
import { isBillingAdmin } from './business/Subscription';
import { getDiffDaysFromNow } from './util/TimeUtil';
import dayjs from 'dayjs';
import { AlertColor } from '@mui/material/Alert/Alert';
import { getPaymentMethod } from './rest/BackendApi';
import StripeButton from './components/StripeButton';

export const ADD_PAYMENT_METHOD_REDIRECT = '/api/subscription/payment-method-redirect';

export type TrialBanner = { severity: AlertColor; title: string; text: string; expired: boolean };

/**
 * Banner shown to billing admins during a trial without a card. diffInDays comes from getDiffDaysFromNow:
 * -1 once the end date has passed, 0 when it ends within 24 hours.
 */
export function getTrialBanner(diffInDays: number): TrialBanner {
  const keepAccess = 'Add a card to keep your access.';
  if (isNaN(diffInDays)) {
    return { severity: 'info', title: 'Your trial is ending soon.', text: keepAccess, expired: false };
  }
  if (diffInDays < 0) {
    return { severity: 'error', title: 'Your trial has ended.', text: 'Add a card to continue using BAS Ready.', expired: true };
  }
  if (diffInDays === 0) {
    return { severity: 'error', title: 'Your trial ends today.', text: keepAccess, expired: false };
  }
  if (diffInDays === 1) {
    return { severity: 'error', title: 'Your trial ends tomorrow.', text: keepAccess, expired: false };
  }
  return {
    severity: diffInDays < 4 ? 'warning' : 'info',
    title: `Your trial ends in ${diffInDays + 1} days.`,
    text: keepAccess,
    expired: false,
  };
}

export default function Header() {
  const userContext = useContext(UserContext);
  const user = userContext?.user;
  const onTrial = user != null && isBillingAdmin(user) && user.firm?.subscription.status == 'TRIAL';
  // Trials start without a card, so only remind the billing admin until they've added one
  const [hasCard, setHasCard] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (onTrial) {
      getPaymentMethod()
        .then(response => setHasCard(response.hasPaymentMethod))
        .catch(() => setHasCard(false));
    }
  }, [onTrial]);

  if (!user?.firm || !onTrial || hasCard !== false) {
    return null;
  }

  const banner = getTrialBanner(getDiffDaysFromNow(dayjs.utc(user.firm.subscription.trialEndDate)));

  return (
    <Alert
      severity={banner.severity}
      sx={{ mb: 1, alignItems: 'center' }}
      action={
        <StripeButton href={ADD_PAYMENT_METHOD_REDIRECT} size="small" color="inherit" variant="outlined" sx={{ whiteSpace: 'nowrap' }}>
          Add payment details
        </StripeButton>
      }
    >
      <b>{banner.title}</b> {banner.text}
    </Alert>
  );
}
